import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ImageUploader from '../components/ui/image-uploader';
import { Building2, Plus, Trash2, Store, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = () => ({
  empresa_name: '', brand_name: '', rif: '', warranty_text: '', logo_url: '', login_slug: '',
  vertical: 'electronics',
  modules: [], sucursales: [{ name: '', slug: '', rif: '' }],
  owner_name: '', owner_email: '', owner_password: '',
  price: '', billing_cycle: 'mensual', trial_days: '', start_date: '', renew_date: '', contract_notes: '',
});

export default function SellLicense() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [f, setF] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.getModulesCatalog().then((r) => setCatalog(r.data.modules || [])).catch(() => {});
    api.getTenants().then((r) => setTenants(r.data || [])).catch(() => {});
    api.getVerticals().then((r) => setVerticals(r.data.verticals || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleModule = (key) => setF((s) => ({ ...s, modules: s.modules.includes(key) ? s.modules.filter((m) => m !== key) : [...s.modules, key] }));
  const setSuc = (i, patch) => setF((s) => { const arr = [...s.sucursales]; arr[i] = { ...arr[i], ...patch }; return { ...s, sucursales: arr }; });
  const addSuc = () => setF((s) => ({ ...s, sucursales: [...s.sucursales, { name: '', slug: '', rif: '' }] }));
  const removeSuc = (i) => setF((s) => ({ ...s, sucursales: s.sucursales.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!f.empresa_name.trim()) return toast.error('SalesPRO: Indica el nombre de la empresa');
    if (!f.sucursales.filter((x) => x.name.trim()).length) return toast.error('SalesPRO: Agrega al menos una sucursal con nombre');
    if (!f.owner_name.trim() || !f.owner_email.trim() || !f.owner_password.trim()) return toast.error('SalesPRO: Completa los datos del propietario');
    if (f.owner_password.length < 6) return toast.error('SalesPRO: La contraseña del propietario debe tener al menos 6 caracteres');
    setBusy(true);
    try {
      const payload = {
        ...f, sucursales: f.sucursales.filter((x) => x.name.trim()),
        price: f.price !== '' ? Number(f.price) : null,
        trial_days: f.trial_days !== '' ? parseInt(f.trial_days, 10) : 0,
      };
      const r = await api.provisionTenant(payload);
      toast.success(r.data.message);
      setF(emptyForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: No se pudo crear la empresa');
    } finally { setBusy(false); }
  };

  const downloadContract = async (t) => {
    try {
      const r = await api.getTenantContract(t.tenant_id);
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('SalesPRO: No se pudo generar el contrato'); }
  };

  const [newSuc, setNewSuc] = useState({});
  const [newSucMode, setNewSucMode] = useState({});
  const addSuctoTenant = async (t) => {
    const name = (newSuc[t.tenant_id] || '').trim();
    if (!name) return toast.error('SalesPRO: Indica el nombre de la sucursal');
    try { await api.addSucursal(t.tenant_id, { name, numbering_mode: newSucMode[t.tenant_id] || 'independent' }); toast.success('SalesPRO: Sucursal agregada'); setNewSuc({ ...newSuc, [t.tenant_id]: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  const toggleSuc = async (c) => {
    try { await api.updateCompany(c.company_id, { active: !(c.active !== false) }); toast.success('SalesPRO: Sucursal actualizada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const [freeCompanies, setFreeCompanies] = useState([]);
  const [linkF, setLinkF] = useState({ empresa_name: '', brand_name: '', owner_email: '', ids: [] });
  useEffect(() => { api.getCompanies().then((r) => setFreeCompanies((r.data || []).filter((c) => !c.tenant_id))).catch(() => {}); }, [tenants]);
  const toggleLinkId = (id) => setLinkF((s) => ({ ...s, ids: s.ids.includes(id) ? s.ids.filter((x) => x !== id) : [...s.ids, id] }));
  const doLink = async () => {
    if (!linkF.empresa_name.trim() || !linkF.ids.length) return toast.error('SalesPRO: Indica nombre y al menos una tienda');
    try { const r = await api.linkExistingTenant({ empresa_name: linkF.empresa_name, brand_name: linkF.brand_name || null, owner_email: linkF.owner_email || null, company_ids: linkF.ids }); toast.success(r.data.message); setLinkF({ empresa_name: '', brand_name: '', owner_email: '', ids: [] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const saveTenantModules = async (t, modules) => {
    try { await api.updateTenantModules(t.tenant_id, modules); toast.success('SalesPRO: Módulos de la empresa actualizados'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  if (user?.role_id !== 'master') return <div className="p-6 text-[#64748B]" data-testid="sell-license-denied">Acceso exclusivo del Super Master.</div>;

  return (
    <div className="space-y-6" data-testid="sell-license-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#022C22] flex items-center gap-2"><Building2 /> Vender Licencia</h1>
        <p className="text-sm text-[#64748B]">Crea una empresa licenciataria con sus sucursales, marca y módulos pagados. Los datos de cada empresa quedan totalmente aislados.</p>
      </div>

      <Card className="border-[#B8860B]/40 bg-[#FFFBEB]" data-testid="license-flow-help">
        <CardContent className="p-4 space-y-2 text-sm">
          <p className="font-bold text-[#022C22]">¿Cómo funciona el modelo?</p>
          <ol className="list-decimal list-inside text-[#334155] text-xs space-y-1">
            <li><b>Master vende licencia</b> → crea <b>Empresa (tenant)</b> con RIF, marca, logo y <b>módulos pagados</b>.</li>
            <li>La Empresa nace con <b>sus sucursales</b> y un <b>Propietario (superadmin)</b>.</li>
            <li>El propietario accede por <b>app.salespr.win/lg/&lt;slug&gt;</b> (o su dominio propio). Solo ve <b>sus</b> sucursales. Nunca mezcla datos con otra empresa.</li>
            <li>Si una empresa quiere agregar una sucursal, se hace desde esta misma pantalla (sección "Empresas con licencia").</li>
            <li>El Master <b>no debe crear empresas sueltas</b> desde el menú Empresas; esa pantalla ahora redirige aquí.</li>
          </ol>
        </CardContent>
      </Card>

      <Card><CardContent className="p-5 space-y-5">
        <h2 className="font-semibold text-[#022C22]">Nueva empresa (licencia)</h2>

        {/* Selector de RUBRO (vertical) */}
        <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="vertical-picker">
          <div>
            <Label className="text-sm font-bold text-[#022C22]">1️⃣ Rubro de esta empresa</Label>
            <p className="text-[11px] text-[#64748B]">Define qué campos y módulos aparecen por default. Celular Beach = Electrónica.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {verticals.map((v) => (
              <button type="button" key={v.key} onClick={() => setF({ ...f, vertical: v.key, modules: v.default_modules || f.modules })}
                data-testid={`vertical-opt-${v.key}`}
                className={`text-left px-3 py-2 rounded-md border text-xs transition-all ${f.vertical === v.key ? 'bg-[#022C22] text-white border-[#022C22] shadow-md -translate-y-0.5' : 'bg-white border-[#E2E8F0] text-[#475569] hover:border-primary/50'}`}>
                <p className="font-bold">{f.vertical === v.key ? '✓ ' : ''}{v.label}</p>
                <p className={`text-[10px] mt-1 ${f.vertical === v.key ? 'text-white/80' : 'text-[#94A3B8]'}`}>{v.description}</p>
                {v.price_hint && <p className={`text-[10px] mt-1 ${f.vertical === v.key ? 'text-white/90 font-semibold' : 'text-primary font-semibold'}`}>Sugerido: ${v.price_hint}/mes</p>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Nombre de la empresa *</Label><Input value={f.empresa_name} onChange={(e) => setF({ ...f, empresa_name: e.target.value })} placeholder="Ej. Tecno Zuliana, C.A." data-testid="lic-empresa-name" /></div>
          <div><Label>Marca en documentos</Label><Input value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} placeholder="Ej. TECNO ZULIANA" data-testid="lic-brand" /></div>
          <div><Label>RIF</Label><Input value={f.rif} onChange={(e) => setF({ ...f, rif: e.target.value })} placeholder="J-123456789" /></div>
          <div><Label>Nota de garantía (opcional)</Label><Input value={f.warranty_text} onChange={(e) => setF({ ...f, warranty_text: e.target.value })} placeholder="Ej. 3 meses por defectos de fábrica" /></div>
          <div><Label>Enlace de acceso (slug)</Label><Input value={f.login_slug} onChange={(e) => setF({ ...f, login_slug: e.target.value })} placeholder="celularbeach → /lg/celularbeach" data-testid="lic-loginslug" /></div>
        </div>

        <div className="rounded-md border border-[#E2E8F0] p-3 bg-[#F8FAFC] grid md:grid-cols-5 gap-2">
          <div><Label className="text-[11px]">Precio licencia ($)</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="49" data-testid="lic-price" /></div>
          <div>
            <Label className="text-[11px]">Ciclo</Label>
            <select className="w-full px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" value={f.billing_cycle} onChange={(e) => setF({ ...f, billing_cycle: e.target.value })}>
              <option value="mensual">Mensual</option><option value="trimestral">Trimestral</option><option value="anual">Anual</option>
            </select>
          </div>
          <div><Label className="text-[11px]">Días de prueba</Label><Input type="number" value={f.trial_days} onChange={(e) => setF({ ...f, trial_days: e.target.value })} placeholder="15" data-testid="lic-trial" /></div>
          <div><Label className="text-[11px]">Inicio</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
          <div><Label className="text-[11px]">Renovación</Label><Input type="date" value={f.renew_date} onChange={(e) => setF({ ...f, renew_date: e.target.value })} /></div>
          <div className="md:col-span-5"><Label className="text-[11px]">Notas del contrato</Label><Input value={f.contract_notes} onChange={(e) => setF({ ...f, contract_notes: e.target.value })} placeholder="Condiciones acordadas con el cliente" /></div>
        </div>
        <div>
          <ImageUploader value={f.logo_url} onChange={(id) => setF({ ...f, logo_url: id || '' })} multiple={false} purpose="company_logo" label="Logo de la empresa (sale en los documentos)" />
        </div>

        <div>
          <Label className="mb-2 block">Módulos pagados</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {catalog.map((m) => (
              <button type="button" key={m.key} onClick={() => toggleModule(m.key)} data-testid={`lic-mod-${m.key}`}
                className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${f.modules.includes(m.key) ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}>
                {f.modules.includes(m.key) ? '✓ ' : ''}{m.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-1">Dashboard y Notificaciones siempre están incluidos.</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Sucursales</Label>
            <Button type="button" size="sm" variant="outline" onClick={addSuc} data-testid="lic-add-suc"><Plus size={14} className="mr-1" /> Agregar sucursal</Button>
          </div>
          <div className="space-y-2">
            {f.sucursales.map((s, i) => (
              <div key={i} className="grid md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end border border-[#F1F5F9] rounded-md p-2" data-testid={`lic-suc-${i}`}>
                <div><Label className="text-[11px]">Nombre *</Label><Input value={s.name} onChange={(e) => setSuc(i, { name: e.target.value })} placeholder="Ej. Sucursal Centro" /></div>
                <div><Label className="text-[11px]">Slug web (opcional)</Label><Input value={s.slug} onChange={(e) => setSuc(i, { slug: e.target.value })} placeholder="centro" /></div>
                <div><Label className="text-[11px]">RIF (opcional)</Label><Input value={s.rif} onChange={(e) => setSuc(i, { rif: e.target.value })} /></div>
                <div>
                  <Label className="text-[11px]">Numeración de recibos</Label>
                  {i === 0 ? (
                    <div className="text-[11px] text-[#64748B] px-2 py-2 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]">Principal (define la secuencia)</div>
                  ) : (
                    <select value={s.numbering_mode || 'independent'} onChange={(e) => setSuc(i, { numbering_mode: e.target.value })} className="w-full border rounded-md px-2 py-2 text-xs" data-testid={`lic-suc-numbering-${i}`}>
                      <option value="independent">Independiente (secuencia propia)</option>
                      <option value="shared">Conjunta con la principal</option>
                    </select>
                  )}
                </div>
                <Button type="button" size="icon" variant="ghost" onClick={() => removeSuc(i)} disabled={f.sucursales.length <= 1}><Trash2 size={16} className="text-red-500" /></Button>
              </div>
            ))}
            <p className="text-[11px] text-[#64748B] mt-1">La numeración <b>conjunta</b> comparte la secuencia de recibos/tickets/notas con la sucursal principal; la <b>independiente</b> lleva su propia numeración. La jerarquía y los accesos no cambian.</p>
          </div>
        </div>

        <div className="rounded-md border border-[#E2E8F0] p-3 bg-[#F8FAFC]">
          <p className="text-xs font-semibold text-[#022C22] mb-2">Propietario de la empresa (accede a todas sus sucursales)</p>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Nombre *</Label><Input value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} data-testid="lic-owner-name" /></div>
            <div><Label>Correo *</Label><Input type="email" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} data-testid="lic-owner-email" /></div>
            <div><Label>Contraseña inicial *</Label><Input value={f.owner_password} onChange={(e) => setF({ ...f, owner_password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="lic-owner-pass" /></div>
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-1">El propietario deberá cambiar la contraseña en su primer ingreso.</p>
        </div>

        <Button onClick={submit} disabled={busy} className="w-full bg-[#022C22]" data-testid="lic-submit"><CheckCircle2 size={16} className="mr-1" /> {busy ? 'Creando…' : 'Crear empresa y vender licencia'}</Button>
      </CardContent></Card>

      <div className="space-y-3">
        <h2 className="font-semibold text-[#022C22]">Empresas con licencia ({tenants.length})</h2>
        {freeCompanies.length > 0 && (
          <Card data-testid="link-existing-card"><CardContent className="p-4 space-y-2">
            <p className="font-semibold text-[#022C22] text-sm">⚠ Empresas sin licencia asignada ({freeCompanies.length})</p>
            <p className="text-[11px] text-[#64748B]">Estas tiendas existen pero NO están conectadas a una licencia (empresa). <b>Enlázalas</b> para agruparlas como una Empresa real, o <b>elimínalas</b> si fueron de prueba.</p>
            <div className="grid md:grid-cols-3 gap-2">
              <Input value={linkF.empresa_name} onChange={(e) => setLinkF({ ...linkF, empresa_name: e.target.value })} placeholder="Nombre empresa (ej. Celular Beach)" data-testid="link-empresa-name" />
              <Input value={linkF.brand_name} onChange={(e) => setLinkF({ ...linkF, brand_name: e.target.value })} placeholder="Marca (opcional)" />
              <Input value={linkF.owner_email} onChange={(e) => setLinkF({ ...linkF, owner_email: e.target.value })} placeholder="Correo del propietario (opcional)" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="orphan-list">
              {freeCompanies.map((c) => (
                <div key={c.company_id} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${linkF.ids.includes(c.company_id) ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}>
                  <button type="button" onClick={() => toggleLinkId(c.company_id)} data-testid={`link-pick-${c.company_id}`}>
                    {linkF.ids.includes(c.company_id) ? '✓ ' : ''}{c.name}
                  </button>
                  <button
                    type="button"
                    title="Eliminar esta empresa huérfana"
                    data-testid={`orphan-delete-${c.company_id}`}
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar la empresa "${c.name}"? Solo se puede si está vacía (sin productos/ventas/clientes).`)) return;
                      try {
                        await api.deleteCompany(c.company_id);
                        toast.success(`"${c.name}" eliminada`);
                        setFreeCompanies((xs) => xs.filter((x) => x.company_id !== c.company_id));
                        setLinkF((s) => ({ ...s, ids: s.ids.filter((id) => id !== c.company_id) }));
                      } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar'); }
                    }}
                    className={`ml-1 ${linkF.ids.includes(c.company_id) ? 'text-red-200 hover:text-white' : 'text-red-500 hover:text-red-700'}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
            <Button size="sm" onClick={doLink} className="bg-[#022C22]" data-testid="link-submit">Enlazar como Empresa</Button>
          </CardContent></Card>
        )}
        {tenants.length === 0 && <p className="text-sm text-[#94A3B8]">Aún no has creado empresas licenciatarias.</p>}
        {tenants.map((t) => (
          <Card key={t.tenant_id} data-testid={`tenant-${t.tenant_id}`}><CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#022C22]">{t.name} <span className="text-[11px] text-[#94A3B8]">· {t.brand_name}</span></p>
                <p className="text-xs text-[#64748B]">{t.owner ? `Propietario: ${t.owner.full_name} (${t.owner.email})` : 'Sin propietario'} · {t.sucursal_count} sucursal(es)</p>
                {t.vertical_label && (
                  <p className="text-[11px] mt-1"><span className="inline-block bg-primary/10 text-[#022C22] font-semibold px-2 py-0.5 rounded-full">🏷️ {t.vertical_label}</span></p>
                )}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{t.active ? 'Activa' : 'Inactiva'}</span>
            </div>
            <div>
              <Button size="sm" variant="outline" onClick={() => downloadContract(t)} data-testid={`contract-${t.tenant_id}`}>Contrato / Bienvenida PDF</Button>
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              {(t.sucursales || []).map((c) => (
                <button key={c.company_id} onClick={() => toggleSuc(c)} title="Activar / Suspender"
                  className={`text-[11px] px-2 py-0.5 rounded inline-flex items-center gap-1 ${c.active === false ? 'bg-red-50 text-red-600 line-through' : 'bg-[#F1F5F9] text-[#475569]'}`}
                  data-testid={`suc-toggle-${c.company_id}`}><Store size={11} /> {c.name}</button>
              ))}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Input value={newSuc[t.tenant_id] || ''} onChange={(e) => setNewSuc({ ...newSuc, [t.tenant_id]: e.target.value })} placeholder="Nueva sucursal…" className="h-8 text-xs max-w-[220px]" data-testid={`add-suc-input-${t.tenant_id}`} />
              <select value={newSucMode[t.tenant_id] || 'independent'} onChange={(e) => setNewSucMode({ ...newSucMode, [t.tenant_id]: e.target.value })} className="h-8 border rounded-md px-2 text-xs" data-testid={`add-suc-numbering-${t.tenant_id}`}>
                <option value="independent">Numeración independiente</option>
                <option value="shared">Numeración conjunta</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => addSuctoTenant(t)} data-testid={`add-suc-btn-${t.tenant_id}`}><Plus size={13} className="mr-1" /> Agregar sucursal</Button>
            </div>
            <div>
              <p className="text-[11px] text-[#94A3B8] mb-1">Módulos de la empresa (clic para activar/desactivar):</p>
              <div className="flex flex-wrap gap-1">
                {catalog.map((m) => {
                  const on = (t.enabled_modules || []).includes(m.key);
                  return (
                    <button key={m.key} type="button" onClick={() => saveTenantModules(t, on ? (t.enabled_modules || []).filter((x) => x !== m.key) : [...(t.enabled_modules || []), m.key])}
                      className={`text-[10px] px-2 py-0.5 rounded border ${on ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#94A3B8]'}`}>{m.label}</button>
                  );
                })}
              </div>
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
